import { useEffect, useMemo, useState } from 'react';
import { shallowEqual } from 'react-redux';
import Cookies from 'js-cookie';

import { Box, ButtonGroup, Heading, Icon, IconButton, Text, TextButton, UpsellBanner } from '@zillow/constellation-10';
import { IconChevronLeftOutline, IconChevronRightOutline } from '@zillow/constellation-icons';

import { useRentalManagerUserFeatureLaunchWebApiHandler_notifications } from '@zg-rentals/rental-manager-env';

import { useSelector } from '../../selectors/useSelector';
import accessUtils from '../../utils/accessUtils';

import FeatureNotificationsCSEvents from '../../model/ClickstreamEvent/FeatureNotificationsCSEvents';
import FeatureLaunchNotification from '../../model/FeatureLaunchNotification';

import { BannerBox } from '../../containers/PropertiesPage/styles';

import type { ReactNode } from 'react';

type Notification = {
  url: string;
  icon: string;
  name: string;
  ctaText: string;
  description: string;
  title: string;
};

const NotificationBanner = () => {
  const { user, cookieEnabledServices } = useSelector(
    (state) => ({
      user: state.user,
      cookieEnabledServices: state.app.cookieEnabledServices,
    }),
    shallowEqual,
  );

  const [notifications, setNotifications] = useState<Array<Notification>>([]);
  const [iconComponent, setIconComponent] = useState<ReactNode>(null);
  const [index, setIndex] = useState(0);

  const { data: { notifications: notificationsData } = {}, isPending } =
    useRentalManagerUserFeatureLaunchWebApiHandler_notifications({});

  const canAccessListingQuality = accessUtils.canAccessListingQuality({
    cookieEnabledServices,
    canAccessZramMultifamilyPropertyTable: user.canAccessZramMultifamilyPropertyTable,
  });

  useEffect(() => {
    if (!isPending && Array.isArray(notificationsData)) {
      const newNotifications = notificationsData
        .map(FeatureLaunchNotification)
        .filter((notif) => Cookies.get(`ZRM_FEATURE_NOTIF_${notif.name}`) === undefined);
      setNotifications(newNotifications);
      if (newNotifications.length > 0) {
        FeatureNotificationsCSEvents.DisplayFeatureNotification(csEventParams);
      }
    }
  }, [isPending]);

  const notification = notifications && notifications[index];

  const csEventParams = useMemo(() => ({ user, notificationTitle: notification?.title }), [user, notification]);

  useEffect(() => {
    if (notification?.icon) {
      const { [notification.icon]: NewIcon } = require('@zillow/constellation');
      if (NewIcon) {
        setIconComponent(<NewIcon />);
      }
    } else if (iconComponent) {
      setIconComponent(null);
    }
  }, [notification]);

  const addCookie = (notifType: string) => {
    // @ts-expect-error
    Cookies.withConverter({ write: (_) => _ }).set(`ZRM_FEATURE_NOTIF_${notifType}`, true);
  };

  const handlePreviousNotification = () => {
    addCookie(notifications[index].name);
    const newIndex = index <= 0 ? notifications.length - 1 : index - 1;
    setIndex(newIndex);
  };

  const handleNextNotification = () => {
    addCookie(notifications[index].name);
    const newIndex = index >= notifications.length - 1 ? 0 : index + 1;
    setIndex(newIndex);
  };

  const handleDismissAll = () => {
    for (let i = 0; i < notifications.length; i++) {
      addCookie(notifications[i].name);
    }
    setNotifications([]);
    FeatureNotificationsCSEvents.DismissAllNotifications(csEventParams);
  };

  const handleDismiss = () => {
    addCookie(notifications[index].name);
    setNotifications([]);
    FeatureNotificationsCSEvents.DismissNotification(csEventParams);
  };

  const handleCTAClick = () => {
    FeatureNotificationsCSEvents.LearnMore(csEventParams);
    window.open(notification.url, '_blank', 'noopener, noreferrer');
  };

  if (isPending || !notification) {
    return null;
  }

  return (
    <BannerBox
      isEnterpriseCustomer={user.isEnterpriseCustomer}
      canAccessListingQuality={canAccessListingQuality}
      data-testid="NotificationBanner"
      data-eventid={
        notifications.length > 0 ? FeatureNotificationsCSEvents.DisplayFeatureNotification.data_event_id : undefined
      }
      data-eventdescription="View feature notification"
    >
      <UpsellBanner
        action={
          notification.ctaText &&
          notification.url && (
            <TextButton onClick={handleCTAClick} data-eventid={FeatureNotificationsCSEvents.LearnMore.data_event_id}>
              {notification.ctaText}
            </TextButton>
          )
        }
        header={
          notification.title && (
            <Heading level={2} textStyle="body-lg-bold">
              {notification.title}
            </Heading>
          )
        }
        body={notification.description && <Text>{notification.description}</Text>}
        media={iconComponent}
        onOpenChange={handleDismiss}
        data-eventid={FeatureNotificationsCSEvents.DismissNotification.data_event_id}
        data-eventdescription="Dismiss feature notification"
      />
      {notifications.length > 1 && (
        <Box
          css={{
            display: 'flex',
            backgroundColor: 'UpsellBanner.Root.soft.bg',
            justifyContent: 'flex-end',
            alignItems: 'center',
          }}
        >
          <TextButton
            textStyle="body-sm-bold"
            onClick={handleDismissAll}
            data-eventid={FeatureNotificationsCSEvents.DismissAllNotifications.data_event_id}
            css={{ paddingRight: '400' }}
          >
            Dismiss all
          </TextButton>
          <ButtonGroup aria-label="notification controls">
            <IconButton
              tone="brand"
              emphasis="bare"
              onClick={handlePreviousNotification}
              icon={
                <Icon>
                  <IconChevronLeftOutline />
                </Icon>
              }
              title="Previous"
            />
            <IconButton
              tone="brand"
              emphasis="bare"
              onClick={handleNextNotification}
              icon={
                <Icon>
                  <IconChevronRightOutline />
                </Icon>
              }
              title="Next"
            />
          </ButtonGroup>
        </Box>
      )}
    </BannerBox>
  );
};

export { NotificationBanner };
export default NotificationBanner;
